import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type P60ModalProps = {
  title: string;
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: P60Values) => Promise<void>;
  /** Subcontractor id (kept for API parity with the parent caller). */
  subcontractorId?: string;
  /** Whether the subcontractor currently has a P45 issued. */
  isIssuedP45?: boolean;
  /** Tax years that have a generated P60 — these are the downloadable/emailable options. */
  p60TaxYears?: number[];
};

export type P60Values = {
  taxYear: number;
};

const P60Modal: React.FC<P60ModalProps> = ({
  title,
  isOpen,
  toggle,
  onSubmit,
  p60TaxYears,
}: P60ModalProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [selected, setSelected] = useState<Option<string> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Options are simply the tax years that have a generated P60, sorted desc.
  const taxYearOptions = useMemo(
    () =>
      (p60TaxYears ?? [])
        .map((taxYear) => ({
          label: isIreland ? `${taxYear}` : `${taxYear} - ${taxYear + 1}`,
          value: taxYear,
        }))
        .sort((a, b) => b.value - a.value),
    [isIreland, p60TaxYears],
  );

  // Auto-select the most recent eligible tax year (first option, sorted desc).
  // If the previously-selected year is no longer in options, fall back to first.
  useEffect(() => {
    if (taxYearOptions.length === 0) {
      if (selected) setSelected(null);
      return;
    }
    const first = taxYearOptions[0];
    const firstOption: Option<string> = {
      label: first.label,
      value: first.value.toString(),
    };
    if (!selected) {
      setSelected(firstOption);
      return;
    }
    const stillValid = taxYearOptions.some(
      (opt) => opt.value.toString() === selected.value,
    );
    if (!stillValid) setSelected(firstOption);
  }, [taxYearOptions, selected]);

  const handleSubmit = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ taxYear: parseInt(selected.value, 10) });
      setSelected(null);
      toggle();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={() => {
        setSelected(null);
        toggle();
      }}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: () => {
            setSelected(null);
            toggle();
          },
        },
        {
          name: messages['common.submit'],
          buttonType: 'primary',
          onClick: handleSubmit,
          loading: isSubmitting,
          disabled: isSubmitting || !selected,
        },
      ]}
      minHeight="150px"
    >
      <AppSelect
        options={taxYearOptions.map((opt) => ({
          label: opt.label,
          value: opt.value.toString(),
        }))}
        value={selected}
        onChange={(val: Option<string>) => setSelected(val)}
        placeholder={messages['common.selectTaxYear'] ?? 'Select Tax Year'}
        className="w-full !min-w-0"
      />
    </AppModalContainer>
  );
};

export default P60Modal;
